import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
// Images
import { Icons } from "@/assets/images";
// Constants
import {
  headerSubMenu,
  Modules,
  PermissionsForRoute,
  ROUTES,
} from "@/shared/constants";
import HeaderTrialIndicator from "@/components/Layouts/HeaderTrialIndicator";
import { useLogout } from "@/shared/hooks/useLogout";
import { useTrialExpiry } from "@/shared/hooks/useTrialExpiry";
import { getInitials, toTitleCase } from "@/shared/utils/utils";
import commonServices from "@/shared/services/common/commonServices";
import { usePermission } from "@/shared/hooks/usePermission";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { showToastDynamic } from "@/shared/utils/toast.util";
import { applyThemeClass } from "@/shared/helper/theme.helper";
import { setTheme, setUserSettings } from "@/store/slices/profileSlice";
import { ThemeType } from "@/shared/type/type";
import {
  HeaderAvatarProps,
  HeaderDropdownKey,
  HeaderSubMenuItem,
  UserData,
} from "@/shared/type/Header.type";
import { FailedNotification } from "@/features/NotificationLog/types/Notification.type";
import { useNotificationDropdown } from "@/features/NotificationLog/hooks";
import NotificationDropdown from "@/features/NotificationLog/components/NotificationDropdown";
import { useDataValidationNotifications } from "@/features/DataValidationNotification/hooks/useDataValidationNotifications";
import DataValidationNotificationDropdown from "@/features/DataValidationNotification/components/DataValidationNotificationDropdown";
import CanAccess from "@/shared/helper/CanAccess";

const HeaderAvatar = ({
  profilePicture,
  userName,
  userInitials,
  showBorder = false,
}: HeaderAvatarProps): React.ReactElement => {
  const wrapperClassName = `flex size-8 xs:size-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-hover ${
    showBorder ? "border border-border" : ""
  }`;

  return (
    <span className={wrapperClassName}>
      {profilePicture ? (
        <img
          src={profilePicture}
          alt={userName || "Profile picture"}
          className="size-full object-cover"
        />
      ) : (
        <span className="inline-flex size-full items-center justify-center font-semibold text-xs xs:text-sm tracking-wide">
          {userInitials}
        </span>
      )}
    </span>
  );
};

const Header = ({
  sidebarClose,
  setSidebarClose,
  setOpenValue,
}: {
  sidebarClose: boolean;
  setSidebarClose: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenValue: React.Dispatch<React.SetStateAction<string>>;
}): React.ReactElement => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [activeHeaderDropdown, setActiveHeaderDropdown] =
    useState<HeaderDropdownKey>(null);
  const { isSuperAdmin } = usePermission();
  const { notifications, totalCount: notificationCount } =
    useNotificationDropdown(isSuperAdmin);
  const {
    notifications: dataValidationNotifications,
    totalCount: dataValidationTotalCount,
    refreshNotifications: refreshDataValidationNotifications,
  } = useDataValidationNotifications(isSuperAdmin);

  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.profile.theme);
  const settings = useAppSelector((state) => state.profile.settings);
  const { logout } = useLogout();
  const { showIndicator, badgeLabel, formattedExpiry } = useTrialExpiry();

  const openHeaderMenu = activeHeaderDropdown === "profile";
  const openNotificationDropdown =
    activeHeaderDropdown === "systemNotifications";
  const openDataValidationDropdown =
    activeHeaderDropdown === "dataValidationNotifications";

  const closeHeaderDropdown = useCallback((): void => {
    setActiveHeaderDropdown(null);
  }, []);

  const toggleHeaderDropdown = useCallback(
    (dropdownKey: Exclude<HeaderDropdownKey, null>): void => {
      setActiveHeaderDropdown((currentDropdown) =>
        currentDropdown === dropdownKey ? null : dropdownKey,
      );
    },
    [],
  );

  /* ---------------- THEME ---------------- */
  const handleThemeChange = useCallback(async (): Promise<void> => {
    const nextTheme: ThemeType = theme === "dark" ? "light" : "dark";
    const previousTheme: ThemeType = theme === "dark" ? "dark" : "light";

    dispatch(setTheme(nextTheme));
    applyThemeClass(nextTheme);

    if (!settings) {
      return;
    }

    const nextSettings = {
      ...settings,
      themeType: nextTheme,
    };

    dispatch(setUserSettings(nextSettings));

    try {
      const response = await commonServices.saveUserSettings(nextSettings);
      if (!response.success) {
        dispatch(setTheme(previousTheme));
        dispatch(setUserSettings(settings));
        applyThemeClass(previousTheme);
        showToastDynamic(
          response.message || "Failed to save theme preference.",
          "error",
        );
      }
    } catch {
      dispatch(setTheme(previousTheme));
      dispatch(setUserSettings(settings));
      applyThemeClass(previousTheme);
      showToastDynamic("Failed to save theme preference.", "error");
    }
  }, [dispatch, settings, theme]);

  /* ---------------- LOGOUT ---------------- */
  const handleLogout = useCallback(async (): Promise<void> => {
    closeHeaderDropdown();
    await logout();
  }, [closeHeaderDropdown, logout]);

  /* ---------------- DERIVED USER DATA (NO STATE) ---------------- */
  const activeAccount = React.useMemo(
    () => instance.getActiveAccount() ?? accounts[0],
    [accounts, instance],
  );

  /* ---------------- DERIVED USER DATA (NO STATE) ---------------- */
  const userData = React.useMemo(() => {
    const claims = activeAccount?.idTokenClaims as
      | { userData?: unknown }
      | undefined;
    const raw = claims?.userData;

    if (!raw || typeof raw !== "string") {
      return { userName: "", userEmail: "" };
    }

    try {
      const parsed: UserData = JSON.parse(raw);
      return {
        userName: parsed.user_name ?? "",
        userEmail: parsed.email ?? "",
      };
    } catch {
      return { userName: "", userEmail: "" };
    }
  }, [activeAccount]);

  /* ---------------- DERIVE INITIALS WITHOUT STATE ---------------- */
  const userInitials = userData.userName ? getInitials(userData.userName) : "";

  const handleNotificationDropdownClose = useCallback((): void => {
    closeHeaderDropdown();
  }, [closeHeaderDropdown]);

  const handleNotificationBellClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      toggleHeaderDropdown("systemNotifications");
      event.stopPropagation();
    },
    [toggleHeaderDropdown],
  );

  const handleNotificationClick = useCallback(
    (notification: FailedNotification): void => {
      navigate(ROUTES.logs.systemLogs, {
        state: {
          preselectedActivity: notification.activityName,
        },
      });
    },
    [navigate],
  );

  const handleDataValidationDropdownClose = useCallback((): void => {
    closeHeaderDropdown();
  }, [closeHeaderDropdown]);

  const handleDataValidationIconClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      toggleHeaderDropdown("dataValidationNotifications");
      event.stopPropagation();
    },
    [toggleHeaderDropdown],
  );

  const handleProfileMenuClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      toggleHeaderDropdown("profile");
      event.stopPropagation();
    },
    [toggleHeaderDropdown],
  );

  /* ---------------- CLICK OUTSIDE DROPDOWN CLOSES MENU ---------------- */
  useEffect(() => {
    const handleClickOutside = (): void => {
      if (activeHeaderDropdown === "profile") {
        closeHeaderDropdown();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return (): void => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activeHeaderDropdown, closeHeaderDropdown]);

  return (
    <header className="header-section">
      <div className="flex items-center justify-between px-4 xs:px-5 bg-white py-1 xs:py-2.5">
        <div className="flex items-center gap-3.5 xs:gap-6.75">
          <Link
            className="max-xs:max-w-26.25 max-w-40 max-h-11.25 max-md:order-1"
            to={ROUTES.dashboard.root}
          >
            <img
              className="object-contain"
              src={
                theme === "dark"
                  ? settings?.darkLogo || Icons.darkSiteLogoIcon
                  : settings?.logo || Icons.siteLogoIcon
              }
              alt="Automation Industries Logo"
            />
          </Link>
          <button
            onClick={() => {
              setSidebarClose(!sidebarClose);
              setOpenValue("");
            }}
            className="h-4 w-4 relative cursor-pointer border border-transparent focus-visible:border-foreground"
          >
            <div
              className={`absolute top-0 left-0 w-3 border-t-2 border-foreground rounded-full ${sidebarClose ? "left-[calc(100%-12px)]" : ""} transition-all duration-500`}
            />
            <div className="absolute top-1/2 -translate-y-[50%] left-0 w-full border-t-2 border-foreground rounded-full transition-all duration-500" />
            <div
              className={`absolute bottom-0 left-0 w-1.5 border-t-2 border-foreground rounded-full ${sidebarClose ? "left-[calc(100%-6px)]" : ""} transition-all duration-500`}
            />
          </button>
        </div>
        <div className="flex max-[376px]:gap-1 gap-2.5">
          {showIndicator && (
            <HeaderTrialIndicator
              badgeLabel={badgeLabel}
              formattedExpiry={formattedExpiry}
            />
          )}
          <div className="flex flex-none gap-0.5 items-center right-block">
            <button
              onClick={(e) => {
                void handleThemeChange();
                e.stopPropagation();
              }}
              className="flex-none rounded-[50%] p-3 hover:bg-background max-md:hidden border border-transparent focus-visible:border-foreground"
            >
              <img
                src={
                  theme === "dark" ? Icons.sunIcon : Icons.headerDarkModeIcon
                }
                alt="Sun-icon"
              />
            </button>
            {isSuperAdmin && (
              <>
                <div className="hasNotification relative flex-none">
                  <button
                    onClick={handleDataValidationIconClick}
                    className="flex-none rounded-[50%] p-3 hover:bg-background max-md:hidden border border-transparent focus-visible:border-foreground relative"
                  >
                    {dataValidationTotalCount > 0 && (
                      <span className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-destructive text-pure-white text-[10px] font-semibold leading-4 text-center">
                        {dataValidationTotalCount > 99
                          ? "99+"
                          : dataValidationTotalCount}
                      </span>
                    )}
                    <img
                      className="object-cover"
                      src={Icons.headerVerifiedIcon}
                      alt="Verified-icon"
                    />
                  </button>
                  <DataValidationNotificationDropdown
                    isOpen={openDataValidationDropdown}
                    onClose={handleDataValidationDropdownClose}
                    notifications={dataValidationNotifications}
                    onUpdated={refreshDataValidationNotifications}
                  />
                </div>
                <div className="hasNotification relative flex-none">
                  <button
                    onClick={handleNotificationBellClick}
                    className="rounded-[50%] p-3 hover:bg-background border border-transparent focus-visible:border-foreground relative"
                  >
                    {notificationCount > 0 && (
                      <span className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-destructive text-pure-white text-[10px] font-semibold leading-4 text-center">
                        {notificationCount > 99 ? "99+" : notificationCount}
                      </span>
                    )}
                    <img src={Icons.headerBellInitialIcon} alt="Bell-icon" />
                  </button>
                  <NotificationDropdown
                    isOpen={openNotificationDropdown}
                    onClose={handleNotificationDropdownClose}
                    notifications={notifications}
                    totalCount={notificationCount}
                    onNotificationClick={handleNotificationClick}
                  />
                </div>
              </>
            )}
          </div>
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            ref={dropdownRef}
            className="flex items-center relative"
          >
            <button
              onClick={handleProfileMenuClick}
              aria-haspopup="menu"
              aria-expanded={openHeaderMenu}
              aria-controls="header-profile-menu"
              className="rounded-[50%] cursor-pointer border border-border focus-visible:border-foreground"
            >
              <HeaderAvatar
                profilePicture={settings?.profilePicture}
                userName={userData.userName}
                userInitials={userInitials}
              />
            </button>
            {openHeaderMenu && (
              <div
                id="header-profile-menu"
                role="menu"
                className={`absolute top-full right-0 opacity-0 invisible p-2 rounded-20 bg-white shadow-[-4px_8px_36px_rgba(17,17,17,0.2)] ${openHeaderMenu && "profile-menu"} transition-all duration-200 `}
              >
                <div className="flex items-center gap-2.5 px-2 pt-2 mb-2.5">
                  <HeaderAvatar
                    profilePicture={settings?.profilePicture}
                    userName={userData.userName}
                    userInitials={userInitials}
                    showBorder
                  />

                  <div className="min-w-0 flex-1">
                    <h2 className="text-base text-foreground font-medium leading-[1.18]">
                      {toTitleCase(userData.userName)}
                    </h2>
                    <p className="text-xs text-foreground leading-[1.16]">
                      {userData.userEmail}
                    </p>
                  </div>
                </div>

                <ul role="menu">
                  {headerSubMenu.map((subMenu: HeaderSubMenuItem, index) => {
                    const menuIcon =
                      theme === "dark" && subMenu.menuListDarkIcon
                        ? subMenu.menuListDarkIcon
                        : subMenu.menuListIcon;
                    const isLogout = subMenu.menuListTxt === "Log Out";
                    const isInsightsPreferences =
                      subMenu.menuListTxt === "Insights Preferences";
                    const isAcknowledge = subMenu.menuListTxt === "Acknowledge";

                    if (
                      !isSuperAdmin &&
                      (isAcknowledge ||
                        subMenu.btn === "data-validation-notifications")
                    ) {
                      return null;
                    }

                    const menuItem = subMenu.btn ? (
                      <button
                        onClick={(e) => {
                          if (subMenu.btn === "theme-toggle") {
                            void handleThemeChange();
                          }

                          if (subMenu.btn === "data-validation-notifications") {
                            setActiveHeaderDropdown(
                              "dataValidationNotifications",
                            );
                          }

                          e.stopPropagation();
                        }}
                        role="menuitem"
                        className="text-sm w-full py-2.5 px-2 flex items-center gap-2.5 md:hidden border border-transparent focus-visible:border-foreground"
                      >
                        <img
                          className="object-cover header-submenu-icons size-4"
                          src={menuIcon}
                          alt={subMenu.menuListIconAlt}
                        />
                        {subMenu.menuListTxt}
                      </button>
                    ) : (
                      <Link
                        to={subMenu.menuListLink ?? "#"}
                        onClick={(e) => {
                          if (isLogout) {
                            e.preventDefault();
                            void handleLogout();
                            return;
                          }

                          closeHeaderDropdown();
                        }}
                        role="menuitem"
                        target={
                          subMenu.menuListLink === ROUTES.header.contactSupport
                            ? "_blank"
                            : undefined
                        }
                        rel={
                          subMenu.menuListLink === ROUTES.header.contactSupport
                            ? "noopener noreferrer"
                            : undefined
                        }
                      >
                        <div className="flex justify-between py-2.5 px-2 items-center">
                          <div className="flex items-end gap-2.5">
                            <div className="w-4">
                              <img
                                className="object-cover header-submenu-icons"
                                src={menuIcon}
                                alt={subMenu.menuListIconAlt}
                              />
                            </div>

                            <p className="text-sm leading-none text-foreground">
                              {subMenu.menuListTxt}
                            </p>
                          </div>

                          <div className="w-3 opacity-0 group-hover:opacity-100 transition-all duration-500">
                            <img
                              className="object-cover header-submenu-arrow"
                              src={Icons.rightArrowIcon}
                              alt="Right Arrow Icon"
                            />
                          </div>
                        </div>
                      </Link>
                    );

                    return (
                      <li
                        key={index}
                        className="group rounded-10 hover:bg-bg-hover transition-all duration-500"
                      >
                        {isInsightsPreferences ? (
                          <CanAccess
                            module={Modules.metrixInsights}
                            permission={PermissionsForRoute.view}
                          >
                            {menuItem}
                          </CanAccess>
                        ) : (
                          menuItem
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default React.memo(Header);
